import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormSelectField, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { IconName } from '@optimus-web/icons';
import {
    Avatar,
    BareIconButton as StyledClearButton,
    debounce,
    DebounceTimeoutValues,
    Stack,
    Tag,
    DropdownItem,
} from '@optimus-web/core';
import { ProfileDataDiscriminator } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { useGetAvatarSrc } from '@mews-commander/utils';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useEnterpriseProfilesSearch } from '../../queries';
import { FieldNames } from '../FieldNames';
import { AddUserFormValues } from './useAddUserForm';

interface Props {
    excludedUserId?: string;
    preselectedProfileId?: string;
}

export const SuperiorSearchField: React.FC<Props> = ({ excludedUserId, preselectedProfileId = '' }) => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    const [search, setSearch] = useState<string>(preselectedProfileId);

    const { data, isFetching } = useEnterpriseProfilesSearch({ searchToken: search });
    const getAvatarSrc = useGetAvatarSrc();

    const selectOptions = data?.profiles?.map(({ id, name, data: profileData }) => {
        if (profileData.discriminator === ProfileDataDiscriminator.EnterpriseProfile) {
            const { email, userId, enterpriseId } = profileData.value;

            if (userId === excludedUserId) {
                return null;
            }

            const matchedUser = data?.users?.filter((user) => user.id === userId)[0];

            return (
                <DropdownItem
                    data-test-select-item-value={id}
                    key={id}
                    value={id}
                    label={<SuperiorName name={name} isCentralUser={enterpriseId !== currentEnterpriseId} />}
                    description={email}
                    leftDetail={<Avatar title={name} src={getAvatarSrc(matchedUser?.imageId)} size="medium" />}
                />
            );
        }

        return null;
    });

    const onSearch = debounce((searchToken: string) => {
        setSearch(searchToken);
    }, DebounceTimeoutValues.textInput);

    const displayMapper = (component: React.ReactElement) => (
        <>
            {component.props.label.props.name} {UnicodeEntity.Dash} {component.props.description}
        </>
    );

    return (
        <FormSelectField
            label={<LocalizedText textKey={TextKey.Supervisor} />}
            loader={isFetching}
            name={FieldNames.SuperiorProfileId}
            onSearchChange={onSearch}
            iconName={IconName.Search}
            suffix={<ClearButton setSearch={setSearch} />}
            displayMapper={displayMapper}
        >
            {selectOptions}
        </FormSelectField>
    );
};

const ClearButton = ({ setSearch }: { setSearch: (searchToken: string) => void }) => {
    const { setValue, watch } = useFormContext<AddUserFormValues>();

    const searchValue = watch(FieldNames.SuperiorProfileId);

    const resetField = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setValue(FieldNames.SuperiorProfileId, '', { shouldDirty: true });
    };

    return searchValue ? (
        <Stack alignment="center">
            <StyledClearButton
                size="small"
                icon={IconName.CrossClose}
                onClick={(e) => {
                    resetField(e);
                    setSearch('');
                }}
            />
        </Stack>
    ) : null;
};

const SuperiorName = ({ name, isCentralUser }: { name: string; isCentralUser: boolean }) => (
    <Stack spacing="150">
        <>{name}</>
        {isCentralUser ? (
            <Tag>
                <LocalizedText textKey={TextKey.PortfolioUser} />
            </Tag>
        ) : null}
    </Stack>
);
